/**
 * AutoMatchPreviewModal Component
 *
 * Displays a preview of auto-matched field mappings for user review.
 * Users can select/deselect individual matches before applying them.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Hash, Type, ToggleLeft, ArrowRight, Wand2, CheckSquare, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SchemaField } from '@/stores/schemas/schema-store';
import {
  type MatchResult,
  getMatchSummary,
  getConfidenceBadgeClass,
  getMatchTypeLabel,
} from '@/utils/autoMatchFields';

interface AutoMatchPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matches: MatchResult[];
  onApply: (selectedMatches: MatchResult[]) => void;
}

// Type icon component
const TypeIcon: React.FC<{ type: SchemaField['type']; className?: string }> = ({ type, className }) => {
  switch (type) {
    case 'NUMBER':
      return <Hash className={cn('h-3.5 w-3.5 text-blue-500', className)} />;
    case 'BOOLEAN':
      return <ToggleLeft className={cn('h-3.5 w-3.5 text-purple-500', className)} />;
    case 'STRING':
    default:
      return <Type className={cn('h-3.5 w-3.5 text-green-500', className)} />;
  }
};

export const AutoMatchPreviewModal: React.FC<AutoMatchPreviewModalProps> = ({
  open,
  onOpenChange,
  matches,
  onApply,
}) => {
  // Track selection state for each match (initialized from match.selected)
  const [selections, setSelections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    matches.forEach((match) => {
      initial[match.schemaField] = match.selected;
    });
    return initial;
  });

  // Reset selections when matches change (modal reopened)
  React.useEffect(() => {
    const initial: Record<string, boolean> = {};
    matches.forEach((match) => {
      initial[match.schemaField] = match.selected;
    });
    setSelections(initial);
  }, [matches]);

  // Get summary statistics
  const summary = useMemo(() => getMatchSummary(matches), [matches]);

  // Count selected items
  const selectedCount = useMemo(() => {
    return Object.entries(selections).filter(
      ([fieldName, selected]) => selected && matches.find((m) => m.schemaField === fieldName)?.sourcePath
    ).length;
  }, [selections, matches]);

  // Toggle individual selection
  const toggleSelection = (fieldName: string) => {
    setSelections((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Select all (only items with matches)
  const selectAll = () => {
    const newSelections: Record<string, boolean> = {};
    matches.forEach((match) => {
      newSelections[match.schemaField] = match.sourcePath !== null;
    });
    setSelections(newSelections);
  };

  // Deselect all
  const deselectAll = () => {
    const newSelections: Record<string, boolean> = {};
    matches.forEach((match) => {
      newSelections[match.schemaField] = false;
    });
    setSelections(newSelections);
  };

  // Handle apply
  const handleApply = () => {
    const selectedMatches = matches.map((match) => ({
      ...match,
      selected: selections[match.schemaField] ?? false,
    }));
    onApply(selectedMatches);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Auto Match Fields
          </DialogTitle>
          <DialogDescription>
            Matched {summary.matched} of {summary.total} fields.
            {summary.highConfidence > 0 && (
              <span className="ml-1">
                ({summary.highConfidence} high confidence
                {summary.mediumConfidence > 0 && `, ${summary.mediumConfidence} medium`})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Field list */}
        <div className="px-6">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 pb-2 border-b">
            <div className="col-span-1"></div>
            <div className="col-span-4">Schema Field</div>
            <div className="col-span-1 text-center"></div>
            <div className="col-span-4">Source Variable</div>
            <div className="col-span-2 text-center">Match</div>
          </div>

          {/* Scrollable list */}
          <div className="max-h-[350px] overflow-y-auto py-2">
            {matches.map((match) => {
              const isSelected = selections[match.schemaField] ?? false;
              const hasMatch = match.sourcePath !== null;

              return (
                <div
                  key={match.schemaField}
                  className={cn(
                    'grid grid-cols-12 gap-2 items-center py-2 border-b border-gray-100 last:border-b-0',
                    !hasMatch && 'opacity-60'
                  )}
                >
                  {/* Checkbox */}
                  <div className="col-span-1 flex justify-center">
                    <Checkbox
                      checked={isSelected && hasMatch}
                      onCheckedChange={() => hasMatch && toggleSelection(match.schemaField)}
                      disabled={!hasMatch}
                    />
                  </div>

                  {/* Schema field name with type icon */}
                  <div className="col-span-4 flex items-center gap-1.5 min-w-0">
                    <TypeIcon type={match.schemaFieldType} />
                    <span className="text-sm font-medium truncate" title={match.schemaField}>
                      {match.schemaField}
                    </span>
                  </div>

                  {/* Arrow */}
                  <div className="col-span-1 flex justify-center">
                    <ArrowRight className={cn('h-4 w-4', hasMatch ? 'text-gray-400' : 'text-gray-300')} />
                  </div>

                  {/* Source variable */}
                  <div className="col-span-4">
                    {hasMatch ? (
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded truncate block" title={match.sourcePath!}>
                        {match.sourcePath}
                      </code>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No match found</span>
                    )}
                  </div>

                  {/* Confidence badge */}
                  <div className="col-span-2 flex justify-center">
                    <Badge
                      variant="outline"
                      className={cn('text-[10px] px-1.5 py-0', getConfidenceBadgeClass(match.confidence))}
                    >
                      {getMatchTypeLabel(match.matchType)}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          {/* Left side: Select all / Deselect all */}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs">
              <CheckSquare className="h-3.5 w-3.5 mr-1" />
              Select All
            </Button>
            <Button variant="ghost" size="sm" onClick={deselectAll} className="text-xs">
              <Square className="h-3.5 w-3.5 mr-1" />
              Deselect All
            </Button>
          </div>

          {/* Right side: Cancel / Apply */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={selectedCount === 0}>
              Apply Selected ({selectedCount})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoMatchPreviewModal;
